import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import PlanCard from "./PlanCard";
import PlanCardSkeleton from "./PlanCardSkeleton";

const BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface Step {
  id: number;
  order: number;
  place: string;
  place_name: string;
  activity: string;
  start_time: string | null;
  duration_minutes: number;
  suggestion: string;
  first_image_url?: string | null;
}

interface Plan {
  id: number;
  name: string;
  description: string;
  suggested_season: string;
  suggested_months: string;
  created_at: string;
  steps: Step[];
}

interface PlansResponse {
  success: boolean;
  data: Plan[];
  pagination: {
    count: number;
    next: string | null;
    previous: string | null;
  };
  message: string;
}

interface PlansSectionProps {
  title: string;
  viewAllLink: string;
}

interface PlaceImage {
  image_id: string;
  file_url: string;
  filename: string;
}

interface PlaceResponse {
  success?: boolean;
  data?: {
    place_id: string;
    name: string;
    images: PlaceImage[];
  };
  images?: PlaceImage[];
}

// تابع دریافت تصاویر با تایپ‌های صحیح
const fetchPlaceImages = async (placeId: string): Promise<string | null> => {
  try {
    const res = await fetch(`${BASE_URL}/api/places/${placeId}/`);
    if (!res.ok) return null;
    const data: PlaceResponse = await res.json();
    const imagesArray = data.data?.images || data.images || [];
    return imagesArray.length > 0 ? imagesArray[0].file_url : null;
  } catch {
    return null;
  }
};

const PlansSection: React.FC<PlansSectionProps> = ({ title, viewAllLink }) => {
  const desktopScrollRef = useRef<HTMLDivElement>(null);
  const mobileScrollRef = useRef<HTMLDivElement>(null);

  // دریافت پلن‌ها
  const { data: plans = [], isLoading, isError, error } = useQuery<Plan[]>({
    queryKey: ["plans"],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}/api/plans/`);
      if (!res.ok) throw new Error(`خطا در دریافت ${title}`);
      const json: PlansResponse = await res.json();
      return json.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

  // دریافت تصاویر برای هر استپ
  const { data: plansWithImages = [] } = useQuery<Plan[]>({
    queryKey: ["plans-with-images", plans],
    queryFn: async () => {
      if (plans.length === 0) return [];
      
      const enrichedPlans = await Promise.all(
        plans.map(async (plan: Plan) => {
          const stepsWithImages = await Promise.all(
            plan.steps.map(async (step: Step) => {
              if (step.first_image_url) return step;
              const imageUrl = await fetchPlaceImages(step.place);
              return { ...step, first_image_url: imageUrl };
            })
          );
          return { ...plan, steps: stepsWithImages };
        })
      );
      
      return enrichedPlans;
    },
    enabled: plans.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  const scrollPrev = () => {
    desktopScrollRef.current?.scrollBy({ left: -600, behavior: "smooth" });
  };
  
  const scrollNext = () => {
    desktopScrollRef.current?.scrollBy({ left: 600, behavior: "smooth" });
  };

  const displayPlans = plansWithImages.length > 0 ? plansWithImages : plans;
  const showLoading = isLoading;

  if (isError && !isLoading) {
    return (
      <div className="text-center text-red-500 py-10">
        {(error as Error).message}
      </div>
    );
  }
  
  if (!isLoading && plans.length === 0) return null;

  return (
    <div className="py-1 px-0 desktop:px-16">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 desktop:px-20 tablet:px-20 px-[16px]">
        <h2 className="text-lg tablet:text-2xl desktop:text-3xl font-myYekanDemibold">
          {title}
        </h2>
        <Link
          to={viewAllLink}
          className="flex items-center gap-1 text-[12px] text-gray-800 font-myYekanFaNumMedium"
        >
          مشاهده همه
          <ChevronLeftIcon className="h-4 w-4" />
        </Link>
      </div>

      {/* موبایل */}
      <div className="md:hidden">
        <div
          ref={mobileScrollRef}
          className="flex overflow-x-auto space-x-2 rtl:space-x-reverse px-2 pb-6 scrollbar-hide"
        >
          {showLoading ? (
            <>
              <PlanCardSkeleton />
              <PlanCardSkeleton />
              <PlanCardSkeleton />
            </>
          ) : (
            displayPlans.map((plan: Plan) => (
              <PlanCard key={plan.id} plan={plan} />
            ))
          )}
        </div>
      </div>

      {/* دسکتاپ */}
      <div className="hidden md:block relative px-12">
        <div className="flex items-center">
          <button
            onClick={scrollPrev}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition shrink-0"
          >
            <ChevronRightIcon className="h-6 w-6 text-gray-600" />
          </button>
          <div
            ref={desktopScrollRef}
            className="flex overflow-x-auto space-x-2 rtl:space-x-reverse pb-6 flex-1 scrollbar-hide"
          >
            {showLoading ? (
              <>
                <PlanCardSkeleton />
                <PlanCardSkeleton />
                <PlanCardSkeleton />
                <PlanCardSkeleton />
              </>
            ) : (
              displayPlans.map((plan: Plan) => (
                <PlanCard key={plan.id} plan={plan} />
              ))
            )}
          </div>
          <button
            onClick={scrollNext}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition shrink-0"
          >
            <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
          </button>
        </div>
      </div>
      
      <style>{`
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      `}</style>
    </div>
  );
};

export default PlansSection;